import { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Alert, CircularProgress,
} from '@mui/material';
import {
  PhotoCamera as CameraIcon, CloudUpload as UploadIcon,
  Replay as RetakeIcon, Check as UseIcon, Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import swal from '../utils/swal';

// Resize a data URL down to maxDim (longest side) and re-encode as JPEG.
// Keeps the BLOB column small while preserving recognisable faces.
function compressImage(dataUrl, maxDim = 480, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export default function PhotoCapture({ value, onChange }) {
  const [camOpen, setCamOpen] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { swal.warning('Please choose an image file'); return; }
    if (file.size > 5 * 1024 * 1024)     { swal.warning('Image must be under 5 MB'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const compressed = await compressImage(reader.result);
        onChange(compressed);
      } catch {
        onChange(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box>
      <Box sx={{
        border: '2px dashed', borderColor: 'divider', borderRadius: 2,
        p: 2, textAlign: 'center', bgcolor: '#fafafa',
        minHeight: 220, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', mb: 1.5,
      }}>
        {value ? (
          <Box component="img" src={value} alt="Student"
            sx={{ maxWidth: 160, maxHeight: 160, borderRadius: 2, border: '1px solid #ddd' }} />
        ) : (
          <>
            <CameraIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" fontWeight={600}>Student Photo</Typography>
            <Typography variant="caption" color="text.secondary">Take a photo or upload one</Typography>
          </>
        )}
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button fullWidth variant="contained" size="small" startIcon={<CameraIcon />}
          onClick={() => setCamOpen(true)}>
          {value ? 'Retake' : 'Camera'}
        </Button>
        <Button fullWidth variant="outlined" size="small" startIcon={<UploadIcon />}
          onClick={() => fileRef.current?.click()}>
          Upload
        </Button>
        {value && (
          <Button fullWidth color="error" size="small" startIcon={<DeleteIcon />}
            onClick={() => onChange('')}>
            Remove
          </Button>
        )}
      </Stack>

      <input ref={fileRef} type="file" accept="image/*" hidden
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />

      <CameraDialog
        open={camOpen}
        onClose={() => setCamOpen(false)}
        onCapture={async (dataUrl) => {
          try {
            const compressed = await compressImage(dataUrl);
            onChange(compressed);
          } catch {
            onChange(dataUrl);
          }
          setCamOpen(false);
        }}
      />
    </Box>
  );
}

// ── Camera Dialog ────────────────────────────────────────────────
function CameraDialog({ open, onClose, onCapture }) {
  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const [error, setError]   = useState('');
  const [loading, setL]     = useState(true);
  const [preview, setPrev]  = useState('');

  // Start / stop camera with the dialog
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(''); setPrev(''); setL(true);

    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Camera not supported in this browser');
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setL(false);
      } catch (e) {
        if (!cancelled) {
          setError(e.name === 'NotAllowedError'
            ? 'Camera access was denied. Please allow camera permission and try again.'
            : (e.message || 'Could not start camera'));
          setL(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [open]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width  = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    setPrev(canvas.toDataURL('image/jpeg', 0.92));
  };

  const retake = () => setPrev('');
  const use    = () => { if (preview) onCapture(preview); };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, py: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CameraIcon />
          <Typography variant="h6" fontWeight={800}>Take a Photo</Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent dividers sx={{ p: 0, bgcolor: '#000' }}>
        {error ? (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            <Typography variant="body2" color="text.secondary">
              You can still upload a photo from your computer.
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            position: 'relative',
            aspectRatio: '4 / 3',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {loading && (
              <Box sx={{ position: 'absolute', zIndex: 1, color: '#fff', textAlign: 'center' }}>
                <CircularProgress sx={{ color: '#fff', mb: 1 }} />
                <Typography variant="caption" sx={{ display: 'block', color: '#fff' }}>
                  Starting camera…
                </Typography>
              </Box>
            )}
            {preview ? (
              <Box component="img" src={preview} alt="Captured"
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <Box component="video" ref={videoRef} autoPlay playsInline muted
                sx={{
                  width: '100%', height: '100%', objectFit: 'cover',
                  transform: 'scaleX(-1)', // mirror like a selfie
                }}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'background.default', gap: 1 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        {preview ? (
          <>
            <Button startIcon={<RetakeIcon />} onClick={retake}>Retake</Button>
            <Button variant="contained" startIcon={<UseIcon />} onClick={use}>Use Photo</Button>
          </>
        ) : (
          <Button variant="contained" startIcon={<CameraIcon />}
            onClick={capture} disabled={loading || !!error}>
            Capture
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
